import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import SideBar from "../../components/Sidebar";
import "../../../node_modules/bootstrap/dist/css/bootstrap.min.css";
import "../../../node_modules/bootstrap/dist/js/bootstrap.min.js";
import { ASK_JOB_COACH } from "../../utils/constants";
import BouncingDotsLoader from "../../components/LoadingDots";
import Chat, { ChatMessage } from "../../components/Chat";
import ChatComposer from "../../components/ChatComposer";
import useWindowWidth from "../../utils/windowWidth";

// import io from "socket.io-client";

function OpportunityBot() {
  const navigate = useNavigate();

  const mainRef = useRef<HTMLHeadingElement>(null);
  const location = useLocation();
  const stateResumeContext = (location.state as { useResumeContext?: boolean } | null)?.useResumeContext;
  const useResumeContext = stateResumeContext !== undefined
    ? stateResumeContext === true
    : localStorage.getItem("resume") === "true";

  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [prompt, setPrompt] = useState("");
  const [sendDisabled, setSendDisabled] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [uuid] = useState(() => crypto.randomUUID());

  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { console.log(chat) }, [chat]);

  function handlePromptSubmit(): void {
    const messageText = prompt.trim();
    if (!messageText || sendDisabled) return;
    setSendDisabled(true);
    setChat((prevChat) => {
      return [...prevChat, { sender: "user", text: messageText, timestamp: new Date().toISOString() }];
    });
    sendChatMessage(messageText);
    setPrompt("");
  }

  const sendChatMessage = async (messageText: string) => {
    try {
      const token = localStorage.getItem('token');

      // Don't add empty message here - wait for first chunk
      const response = await fetch(ASK_JOB_COACH, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: messageText,
          uuid,
          stream: true,
          useResumeContext,
        })
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      if (!response.body) {
        throw new Error("The response did not include a message stream");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let isFirstChunk = true;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);

        // On first chunk: create bot message and hide loading
        if (isFirstChunk) {
          setIsStreaming(true);
          setChat((prev) => [...prev, {
            text: chunk,
            timestamp: new Date().toISOString(),
            sender: "bot",
          }]);
          isFirstChunk = false;
        } else {
          // Subsequent chunks: append to existing message
          setChat((prev) => {
            const updated = [...prev];
            updated[updated.length - 1].text += chunk;
            return updated;
          });
        }
      }

    } catch (error) {
      console.error("Error fetching data:", error);
      setChat((prev) => [...prev, {
        text: "I’m sorry, I couldn’t complete that response. Please try again in a moment.",
        timestamp: new Date().toISOString(),
        sender: "bot",
      }]);
    } finally {
      setSendDisabled(false);
      setIsStreaming(false);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  // NEW: Combined useEffect to fix race condition
  useEffect(() => {
    // Auth check first
    if (
      localStorage.getItem("token") === null ||
      localStorage.getItem("token") === undefined
    ) {
      navigate("/login");
      return;
    }

    document.title = "Career Discovery chatbot - Nclude Portal";

    setChat([{
      text: "Hey there! I'm your Job Coach — I can help you explore career paths, find opportunities, and prepare for your next step. What's on your mind?",
      timestamp: new Date().toISOString(),
      sender: "bot",
    }]);
  }, [navigate]);

  const liveRegionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Set focus on the main content when the route changes
    if (mainRef.current) {
      mainRef.current.focus();
    }
  }, [location]);

  //window size
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 769;


  // if (!socket) {
  //   return <div>Loading...</div>;
  // }

  return (
    <div className="containerr">
      {!isMobile && <Header />}
      <div className="page-container">
        <SideBar activePage="Opportunity" />
        <main className="doc-container agent-chat-page" id="main-content" tabIndex={-1}>
          <div className="agent-chat-heading">
            <div>
              <p className="agent-eyebrow">AI career coach</p>
              <h1 className="display-title" ref={mainRef} tabIndex={-1}>Career Discovery</h1>
              <p className="agent-description">Explore career paths, prepare for applications, and plan your next step.</p>
              {useResumeContext && (
                <p className="agent-description" style={{ marginTop: "0.5rem" }}>
                  Using your saved resume. To replace it, go to{" "}
                  <Link
                    to="/edit-profile"
                    style={{
                      color: "#5030e5",
                      textDecoration: "underline",
                      fontWeight: 600,
                    }}
                  >
                    Profile settings
                  </Link>
                  .
                </p>
              )}
            </div>
            <p className="agent-status" role="status" aria-live="polite">
              <span className={sendDisabled || isStreaming ? "status-dot busy" : "status-dot"} aria-hidden="true" />
              {sendDisabled || isStreaming ? "Responding" : "Online"}
            </p>
          </div>
          <section className="chat-box" aria-label="Career Discovery conversation">
            <div
              className="chat-window-container"
              role="log"
              aria-label="Career Discovery messages"
              aria-live="off"
              aria-relevant="additions text"
              aria-busy={sendDisabled}
              tabIndex={0}
            >
              {chat.length === 0 ? (
                <BouncingDotsLoader />
              ) : (
                <Chat
                  chat={chat}
                  isStreaming={isStreaming}
                  assistantName="Career Coach"
                  announcementRef={liveRegionRef}
                />
              )}
              {sendDisabled && !isStreaming && (
                <div className="assistant-thinking" role="status">
                  <BouncingDotsLoader />
                  <span>Career Coach is thinking…</span>
                </div>
              )}
            </div>
            <div ref={liveRegionRef} aria-live="polite" aria-atomic="true" className="visually-hidden" />
            <ChatComposer
              inputRef={inputRef}
              value={prompt}
              onChange={setPrompt}
              onSubmit={handlePromptSubmit}
              busy={sendDisabled}
              placeholder="Message your career coach"
              assistantName="Career Coach"
            />
          </section>
        </main>
      </div>
    </div>
  );
}

export default OpportunityBot;
